import React, { createContext, useContext, useEffect, useCallback, useState, useRef } from 'react';

const KeyboardNavigationContext = createContext();

export const useKeyboardNavigation = () => {
  const context = useContext(KeyboardNavigationContext);
  if (!context) {
    throw new Error('useKeyboardNavigation must be used within KeyboardNavigationProvider');
  }
  return context;
};

export function KeyboardNavigationProvider({ children }) {
  const [activeZone, setActiveZone] = useState('tabs'); // 'tabs', 'list', 'detail', 'modal'
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedListIndex, setSelectedListIndex] = useState(0);

  const tabsRef = useRef([]);
  const listItemsRef = useRef([]);
  const detailElementsRef = useRef([]);
  const modalElementsRef = useRef([]);

  // Register navigation zones
  const registerTabs = useCallback((tabs) => {
    tabsRef.current = tabs;
  }, []);

  const registerListItems = useCallback((items) => {
    listItemsRef.current = items;
  }, []);

  const registerDetailElements = useCallback((elements) => {
    detailElementsRef.current = elements;
  }, []);

  const registerModalElements = useCallback((elements) => {
    modalElementsRef.current = elements;
  }, []);

  // Focus management
  const focusElement = useCallback((element) => {
    if (element && typeof element.focus === 'function') {
      element.focus();
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  // Navigation functions
  const navigateUp = useCallback(() => {
    switch (activeZone) {
      case 'list':
        // If no items are registered, find them now
        if (listItemsRef.current.length === 0) {
          const items = document.querySelectorAll(
            '.list-item, .master-list-item, ' +
            'tbody tr[tabindex], tbody tr[role="listitem"], ' +
            '.left-panel tbody tr, .list-table tbody tr, ' +
            '.items-table tbody tr'
          );
          listItemsRef.current = Array.from(items);
        }

        if (selectedListIndex > 0) {
          // Clear previous focus
          if (listItemsRef.current[selectedListIndex]) {
            listItemsRef.current[selectedListIndex].classList.remove('keyboard-focus');
          }

          setSelectedListIndex(prev => prev - 1);
          const targetItem = listItemsRef.current[selectedListIndex - 1];
          if (targetItem) {
            targetItem.setAttribute('tabindex', '0');
            targetItem.classList.add('keyboard-focus');
            focusElement(targetItem);
          }
        }
        break;
      case 'detail':
        // Navigate through form fields or detail elements
        const currentDetailIndex = detailElementsRef.current.indexOf(document.activeElement);
        if (currentDetailIndex > 0) {
          focusElement(detailElementsRef.current[currentDetailIndex - 1]);
        }
        break;
      case 'modal':
        // Navigate through modal elements
        const currentModalIndex = modalElementsRef.current.indexOf(document.activeElement);
        if (currentModalIndex > 0) {
          focusElement(modalElementsRef.current[currentModalIndex - 1]);
        }
        break;
      default:
        break;
    }
  }, [activeZone, selectedListIndex, focusElement]);

  const navigateDown = useCallback(() => {
    switch (activeZone) {
      case 'list':
        // If no items are registered, find them now
        if (listItemsRef.current.length === 0) {
          const items = document.querySelectorAll(
            '.list-item, .master-list-item, ' +
            'tbody tr[tabindex], tbody tr[role="listitem"], ' +
            '.left-panel tbody tr, .list-table tbody tr, ' +
            '.items-table tbody tr'
          );
          listItemsRef.current = Array.from(items);
        }

        if (selectedListIndex < listItemsRef.current.length - 1) {
          // Clear previous focus
          if (listItemsRef.current[selectedListIndex]) {
            listItemsRef.current[selectedListIndex].classList.remove('keyboard-focus');
          }

          setSelectedListIndex(prev => prev + 1);
          const targetItem = listItemsRef.current[selectedListIndex + 1];
          if (targetItem) {
            targetItem.setAttribute('tabindex', '0');
            targetItem.classList.add('keyboard-focus');
            focusElement(targetItem);
          }
        }
        break;
      case 'detail':
        // Navigate through form fields or detail elements
        const currentDetailIndex = detailElementsRef.current.indexOf(document.activeElement);
        if (currentDetailIndex < detailElementsRef.current.length - 1) {
          focusElement(detailElementsRef.current[currentDetailIndex + 1]);
        }
        break;
      case 'modal':
        // Navigate through modal elements
        const currentModalIndex = modalElementsRef.current.indexOf(document.activeElement);
        if (currentModalIndex < modalElementsRef.current.length - 1) {
          focusElement(modalElementsRef.current[currentModalIndex + 1]);
        }
        break;
      default:
        break;
    }
  }, [activeZone, selectedListIndex, focusElement]);

  const navigateLeft = useCallback(() => {
    switch (activeZone) {
      case 'tabs':
        if (selectedTabIndex > 0) {
          setSelectedTabIndex(prev => prev - 1);
          if (tabsRef.current[selectedTabIndex - 1]) {
            tabsRef.current[selectedTabIndex - 1].click();
          }
        }
        break;
      case 'detail':
        // Move focus to list
        setActiveZone('list');
        if (listItemsRef.current[selectedListIndex]) {
          focusElement(listItemsRef.current[selectedListIndex]);
        }
        break;
      default:
        break;
    }
  }, [activeZone, selectedTabIndex, selectedListIndex, focusElement]);

  const navigateRight = useCallback(() => {
    switch (activeZone) {
      case 'tabs':
        if (selectedTabIndex < tabsRef.current.length - 1) {
          setSelectedTabIndex(prev => prev + 1);
          if (tabsRef.current[selectedTabIndex + 1]) {
            tabsRef.current[selectedTabIndex + 1].click();
          }
        }
        break;
      case 'list':
        // Move focus to detail
        setActiveZone('detail');
        if (detailElementsRef.current[0]) {
          focusElement(detailElementsRef.current[0]);
        }
        break;
      default:
        break;
    }
  }, [activeZone, selectedTabIndex, focusElement]);

  const handleEnter = useCallback(() => {
    const activeElement = document.activeElement;

    // If focused on a button, click it
    if (activeElement?.tagName === 'BUTTON' || activeElement?.tagName === 'A') {
      activeElement.click();
      return;
    }

    // If in list zone, select the current item
    if (activeZone === 'list' && listItemsRef.current[selectedListIndex]) {
      listItemsRef.current[selectedListIndex].click();
      // Move to detail zone after selection
      setActiveZone('detail');
      if (detailElementsRef.current[0]) {
        focusElement(detailElementsRef.current[0]);
      }
    }

    // If in tabs zone, activate the selected tab
    if (activeZone === 'tabs' && tabsRef.current[selectedTabIndex]) {
      tabsRef.current[selectedTabIndex].click();
      // Move to list or detail zone after tab selection
      setActiveZone('list');
      if (listItemsRef.current[0]) {
        focusElement(listItemsRef.current[0]);
      }
    }
  }, [activeZone, selectedListIndex, selectedTabIndex, focusElement]);

  const handleEscape = useCallback(() => {
    // Close any open modals
    const modalCloseButton = document.querySelector('.modal-close');
    if (modalCloseButton) {
      modalCloseButton.click();
      setActiveZone('detail');
      return;
    }

    // Move back through zones: modal -> detail -> list -> tabs
    switch (activeZone) {
      case 'modal':
        setActiveZone('detail');
        if (detailElementsRef.current[0]) {
          focusElement(detailElementsRef.current[0]);
        }
        break;
      case 'detail':
        setActiveZone('list');
        if (listItemsRef.current[selectedListIndex]) {
          focusElement(listItemsRef.current[selectedListIndex]);
        }
        break;
      case 'list':
        setActiveZone('tabs');
        if (tabsRef.current[selectedTabIndex]) {
          focusElement(tabsRef.current[selectedTabIndex]);
        }
        break;
      case 'tabs':
        // Already at top level, maybe go to header
        const logoutButton = document.querySelector('.btn-logout');
        if (logoutButton) {
          focusElement(logoutButton);
        }
        break;
      default:
        break;
    }
  }, [activeZone, selectedListIndex, selectedTabIndex, focusElement]);

  // Global keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't interfere with input fields unless it's Escape
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        if (e.key !== 'Escape') {
          return;
        }
      }

      switch (e.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          // If we're in tabs zone and pressing up/down, automatically move to list zone
          if (activeZone === 'tabs') {
            e.preventDefault();
            setActiveZone('list');
            // Find all focusable list items (expanded selectors)
            const listItems = document.querySelectorAll(
              '.list-item, .master-list-item, ' +
              'tbody tr[tabindex], tbody tr[role="listitem"], ' +
              '.left-panel tbody tr, .list-table tbody tr, ' +
              '.items-table tbody tr'
            );

            if (listItems.length > 0) {
              if (e.key === 'ArrowDown') {
                // Focus first item
                const firstItem = listItems[0];
                firstItem.setAttribute('tabindex', '0');
                firstItem.focus();
                firstItem.classList.add('keyboard-focus');
                firstItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                setSelectedListIndex(0);
              } else {
                // Focus last item
                const lastItem = listItems[listItems.length - 1];
                lastItem.setAttribute('tabindex', '0');
                lastItem.focus();
                lastItem.classList.add('keyboard-focus');
                lastItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                setSelectedListIndex(listItems.length - 1);
              }

              // Store the list items for navigation
              listItemsRef.current = Array.from(listItems);
            }
          } else {
            e.preventDefault();
            if (e.key === 'ArrowUp') {
              navigateUp();
            } else {
              navigateDown();
            }
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          navigateLeft();
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateRight();
          break;
        case 'Enter':
          if (e.target.tagName !== 'BUTTON' && e.target.tagName !== 'A') {
            e.preventDefault();
            handleEnter();
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleEscape();
          break;
        // Number keys for quick tab navigation
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
          if (e.altKey) {
            e.preventDefault();
            const tabIndex = parseInt(e.key) - 1;
            if (tabsRef.current[tabIndex]) {
              tabsRef.current[tabIndex].click();
              setSelectedTabIndex(tabIndex);
              setActiveZone('tabs');
            }
          }
          break;
        // Tab key to cycle through zones
        case 'Tab':
          e.preventDefault();
          if (e.shiftKey) {
            // Cycle backwards through zones
            switch (activeZone) {
              case 'tabs':
                setActiveZone('detail');
                break;
              case 'list':
                setActiveZone('tabs');
                break;
              case 'detail':
                setActiveZone('list');
                break;
              case 'modal':
                setActiveZone('detail');
                break;
              default:
                break;
            }
          } else {
            // Cycle forward through zones
            switch (activeZone) {
              case 'tabs':
                setActiveZone('list');
                break;
              case 'list':
                setActiveZone('detail');
                break;
              case 'detail':
                setActiveZone('tabs');
                break;
              case 'modal':
                break; // Stay in modal
              default:
                break;
            }
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigateUp, navigateDown, navigateLeft, navigateRight, handleEnter, handleEscape, activeZone]);

  // Auto-detect zone changes based on focus
  useEffect(() => {
    const handleFocusChange = (e) => {
      const target = e.target;

      // Check if focus is in a modal
      if (target.closest('.modal-overlay')) {
        setActiveZone('modal');
        return;
      }

      // Check if focus is in the tabs area
      if (target.closest('.tabs-container') || target.closest('.tab-button')) {
        setActiveZone('tabs');
        return;
      }

      // Check if focus is in the list area (expanded selectors for better detection)
      if (target.closest('.list-container') ||
          target.closest('.master-list') ||
          target.closest('.list-table') ||
          target.closest('.items-table') ||
          (target.closest('table') && target.closest('.left-panel')) ||
          target.closest('tbody tr')) {
        setActiveZone('list');
        return;
      }

      // Check if focus is in the detail area
      if (target.closest('.detail-container') ||
          target.closest('.view-container') ||
          target.closest('.right-panel') ||
          target.closest('.card')) {
        setActiveZone('detail');
        return;
      }
    };

    document.addEventListener('focusin', handleFocusChange);
    return () => document.removeEventListener('focusin', handleFocusChange);
  }, []);

  const value = {
    activeZone,
    setActiveZone,
    selectedTabIndex,
    setSelectedTabIndex,
    selectedListIndex,
    setSelectedListIndex,
    registerTabs,
    registerListItems,
    registerDetailElements,
    registerModalElements,
    focusElement,
    navigateUp,
    navigateDown,
    navigateLeft,
    navigateRight,
    handleEnter,
    handleEscape
  };

  return (
    <KeyboardNavigationContext.Provider value={value}>
      {children}
    </KeyboardNavigationContext.Provider>
  );
}